import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { EmpresaContextProvider, useEmpresaContext } from './context/EmpresaContext';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';
import useDynamicFavicon from './hooks/useDynamicFavicon';

// Pages
import LoginPage from './pages/LoginPage';
import PublicFormPage from './pages/PublicFormPage';
import DashboardPage from './pages/DashboardPage';
import ReclamosPage from './pages/ReclamosPage';
import ProyectosPage from './pages/ProyectosPage';
import SubdivisionesPage from './pages/SubdivisionesPage';
import CustomerJourneyMapPage from './pages/CustomerJourneyMapPage';
import Cliente360ListPage from './pages/Cliente360ListPage';
import Cliente360DetailPage from './pages/Cliente360DetailPage';
import CXConfiguracionPage from './pages/CXConfiguracionPage';
import CXAnalyticsPage from './pages/CXAnalyticsPage';
import HistorialEncuestasPage from './pages/HistorialEncuestasPage';
import AreasFuncionalesPage from './pages/AreasFuncionalesPage';
import UsuariosPage from './pages/UsuariosPage';
import CuentasBloqueadasPage from './pages/CuentasBloqueadasPage';
import DesbloqueoCuentaPage from './pages/DesbloqueoCuentaPage';
import RolesPage from './pages/RolesPage';
import EmpresaPage from './pages/EmpresaPage';
// Módulo Centro de Ayuda (Tickets)
import CentroAyudaTickets from './modules/centroAyuda/TicketsPage';
import CentroAyudaTicketDetalle from './modules/centroAyuda/TicketDetallePage';
import CentroAyudaConfiguracion from './modules/centroAyuda/ConfiguracionPage';
import CentroAyudaEstadisticas from './modules/centroAyuda/EstadisticasPage';
import CentroAyudaAuditoria from './modules/centroAyuda/AuditoriaPage';
import EmpresasPage from './pages/EmpresasPage';
import AuditoriaPage from './pages/AuditoriaPage';
import PerfilPage from './pages/PerfilPage';
import NotificacionesPage from './pages/NotificacionesPage';
import ResolucionPage from './pages/ResolucionPage';
import ResolucionesPage from './pages/ResolucionesPage';
import ConsultaReclamoPage from './pages/ConsultaReclamoPage';
import CambioPasswordObligatorio from './components/CambioPasswordObligatorio';

// Layout
import AdminLayout from './layouts/AdminLayout';
import { primeraRutaPermitida } from './utils/navPermisos';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, requiereCambioPassword, setRequiereCambioPassword } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a1a2e]"></div>
      </div>
    );
  }

  if (!user) {
    // Guardar destino para redirigir tras iniciar sesión (deep-link desde correos)
    try { sessionStorage.setItem('ldrv_redirect_after_login', location.pathname + location.search); } catch (_) {}
    // Prioridad del código de empresa: query param (?empresa=) > localStorage > genérico
    const empresaQuery = new URLSearchParams(location.search).get('empresa');
    const empresaCodigo = empresaQuery || localStorage.getItem('ldrv_empresa_codigo');
    const loginPath = empresaCodigo ? `/login/${empresaCodigo}` : '/login';
    return <Navigate to={loginPath} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.rol)) {
    return <Navigate to={primeraRutaPermitida(user)} replace />;
  }

  if (requiereCambioPassword) {
    return (
      <>
        {children}
        <CambioPasswordObligatorio onSuccess={() => setRequiereCambioPassword(false)} />
      </>
    );
  }

  return children;
};

// Redirect del índice /admin -> primera ruta permitida según permisos del rol.
const AdminIndexRedirect = () => {
  const { user } = useAuth();
  return <Navigate to={primeraRutaPermitida(user)} replace />;
};

// Guard por módulo: si la matriz de permisos no habilita `modulo`, redirige.
const ModuloGuard = ({ modulo, children }) => {
  const { user } = useAuth();
  const permisos = user?.permisos;
  if (permisos && permisos[modulo]?.ver !== true) {
    return <Navigate to={primeraRutaPermitida(user)} replace />;
  }
  return children;
};

// Home redirect - respeta dominio de empresa
const HomeRedirect = () => {
  const { user, loading: authLoading } = useAuth();
  const { esDominioEmpresa, empresaDominio, loading: empresaLoading } = useEmpresaContext();

  if (authLoading || empresaLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a1a2e]"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to={primeraRutaPermitida(user)} replace />;
  }

  // Si es dominio de empresa, mostrar login de esa empresa directamente
  if (esDominioEmpresa && empresaDominio) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to="/login" replace />;
};

function AppRoutes() {
  const { esDominioEmpresa, empresaDominio, loading: empresaLoading } = useEmpresaContext();
  const { empresa } = useAuth();

  // Favicon dinámico: prioridad dominio > empresa logueada
  const faviconCodigo = esDominioEmpresa ? empresaDominio?.codigo : empresa?.codigo;
  const faviconLogo = esDominioEmpresa ? empresaDominio?.logo_url : empresa?.logo_url;
  useDynamicFavicon(faviconCodigo, faviconLogo);

  if (empresaLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a1a2e]"></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Home */}
      <Route path="/" element={<HomeRedirect />} />

      {/* Public Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login/:empresaCodigo" element={<LoginPage />} />
      <Route path="/desbloqueo" element={<DesbloqueoCuentaPage />} />

      {esDominioEmpresa && empresaDominio ? (
        <>
          {/* Rutas públicas para dominio de empresa: sin :empresaCodigo en URL */}
          <Route path="/reclamos" element={<PublicFormPage empresaCodigoProp={empresaDominio.codigo} />} />
          <Route path="/consulta" element={<ConsultaReclamoPage empresaCodigoProp={empresaDominio.codigo} />} />
        </>
      ) : null}

      {/* Rutas públicas clásicas con :empresaCodigo */}
      <Route path="/reclamos/:empresaCodigo" element={<PublicFormPage />} />
      <Route path="/consulta/:empresaCodigo" element={<ConsultaReclamoPage />} />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminIndexRedirect />} />
        <Route path="dashboard" element={<ModuloGuard modulo="dashboard"><DashboardPage /></ModuloGuard>} />
        <Route path="reclamos" element={<ReclamosPage />} />
        <Route 
          path="resoluciones" 
          element={
            <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMINISTRADOR', 'LEGAL', 'SUPERVISOR', 'USUARIO']}>
              <ResolucionesPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="proyectos" 
          element={
            <ProtectedRoute>
              <ModuloGuard modulo="proyectos">
                <ProyectosPage />
              </ModuloGuard>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="proyectos/subdivisiones" 
          element={
            <ProtectedRoute>
              <ModuloGuard modulo="proyectos">
                <SubdivisionesPage />
              </ModuloGuard>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="usuarios" 
          element={
            <ProtectedRoute>
              <ModuloGuard modulo="usuarios">
                <UsuariosPage />
              </ModuloGuard>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="usuarios/bloqueados" 
          element={
            <ProtectedRoute>
              <ModuloGuard modulo="usuarios">
                <CuentasBloqueadasPage />
              </ModuloGuard>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="areas-funcionales" 
          element={
            <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMINISTRADOR']}>
              <AreasFuncionalesPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="roles" 
          element={
            <ProtectedRoute>
              <ModuloGuard modulo="roles">
                <RolesPage />
              </ModuloGuard>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="empresa" 
          element={
            <ProtectedRoute>
              <ModuloGuard modulo="empresa">
                <EmpresaPage />
              </ModuloGuard>
            </ProtectedRoute>
          } 
        />
        <Route
          path="experiencia/journey"
          element={
            <ProtectedRoute>
              <ModuloGuard modulo="cx_journey">
                <CustomerJourneyMapPage />
              </ModuloGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="experiencia/clientes"
          element={
            <ProtectedRoute>
              <ModuloGuard modulo="cx_clientes">
                <Cliente360ListPage />
              </ModuloGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="experiencia/clientes/:id"
          element={
            <ProtectedRoute>
              <ModuloGuard modulo="cx_clientes">
                <Cliente360DetailPage />
              </ModuloGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="experiencia/analytics"
          element={
            <ProtectedRoute>
              <ModuloGuard modulo="cx_analytics">
                <CXAnalyticsPage />
              </ModuloGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="experiencia/encuestas-historial"
          element={
            <ProtectedRoute>
              <ModuloGuard modulo="cx_clientes">
                <HistorialEncuestasPage />
              </ModuloGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="experiencia/configuracion"
          element={
            <ProtectedRoute>
              <ModuloGuard modulo="cx_configuracion">
                <CXConfiguracionPage />
              </ModuloGuard>
            </ProtectedRoute>
          }
        />
        <Route path="perfil" element={<PerfilPage />} />
        <Route path="notificaciones" element={<NotificacionesPage />} />
        <Route
          path="empresas"
          element={
            <ProtectedRoute allowedRoles={['SUPERADMIN']}>
              <EmpresasPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="auditoria"
          element={
            <ProtectedRoute>
              <ModuloGuard modulo="auditoria">
                <AuditoriaPage />
              </ModuloGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="reclamos/:reclamoId/resolucion"
          element={
            <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMINISTRADOR', 'LEGAL', 'SUPERVISOR', 'USUARIO']}>
              <ResolucionPage />
            </ProtectedRoute>
          }
        />

        {/* ============================================================
            CENTRO DE AYUDA (Sistema de Tickets) — visible solo si la empresa
            tiene modulo_tickets_activo. La validación de visibilidad ocurre
            tanto en el sidebar como en cada página del módulo.
            ============================================================ */}
        <Route
          path="centro-ayuda/tickets"
          element={
            <ModuloGuard modulo="ca_tickets">
              <CentroAyudaTickets />
            </ModuloGuard>
          }
        />
        <Route
          path="centro-ayuda/tickets/:ticketId"
          element={
            <ModuloGuard modulo="ca_tickets">
              <CentroAyudaTicketDetalle />
            </ModuloGuard>
          }
        />
        <Route
          path="centro-ayuda/configuracion"
          element={
            <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMINISTRADOR']}>
              <CentroAyudaConfiguracion />
            </ProtectedRoute>
          }
        />
        <Route
          path="centro-ayuda/estadisticas"
          element={
            <ProtectedRoute>
              <ModuloGuard modulo="ca_estadisticas">
                <CentroAyudaEstadisticas />
              </ModuloGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="centro-ayuda/auditoria"
          element={
            <ProtectedRoute>
              <ModuloGuard modulo="ca_auditoria">
                <CentroAyudaAuditoria />
              </ModuloGuard>
            </ProtectedRoute>
          }
        />
      </Route>

      {/* 404 */}
      <Route
        path="*"
        element={
          <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
            <h1 className="font-heading text-4xl font-bold text-slate-900 mb-4">404</h1>
            <p className="text-slate-600 mb-6">Pagina no encontrada</p>
            <a href="/" className="text-[#1a1a2e] hover:underline">
              Volver al inicio
            </a>
          </div>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <EmpresaContextProvider>
        <AuthProvider>
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
          <Toaster position="top-right" />
        </AuthProvider>
      </EmpresaContextProvider>
    </BrowserRouter>
  );
}

export default App;
